import React, { useEffect, useState, useCallback, useRef } from 'react'
import { apiGet } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import dayjs from 'dayjs'
import useToastContext from 'hooks/useToastContext'
import { exportReportToExcel } from 'utils/reportExport'
import { SummaryCard, LoadingState, EmptyState } from './ScheduledVsActualReport'

// ═══ CHART.JS IMPORTS ═══
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar, Doughnut } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
)

// ═══ PAGINATION CONSTANTS ═══
const ITEMS_PER_PAGE = 10;

const OvertimeReport = ({ selectedEmployees, dateRange, onExportReady }) => {
  const [data, setData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const notification = useToastContext()

  // ═══ PAGINATION STATE ═══
  const [currentPage, setCurrentPage] = useState(1)
  
  // ═══ REFS ═══
  const isRegistered = useRef(false);
  const isExportingRef = useRef(false);

  useEffect(() => {
    const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
    const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
    fetchData(start, end, currentPage)
  }, [selectedEmployees, dateRange, currentPage])

  // ═══ RESET PAGE TO 1 WHENEVER FILTER CHANGES ═══
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedEmployees, dateRange])

  const fetchData = async (start, end, page) => {
    setIsLoading(true)
    try {
      const employeeIDs = selectedEmployees.map(e => e.value)
      const payload = {
        startDate: start,
        endDate: end,
        page: page,
        limit: ITEMS_PER_PAGE,
        ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
      }
      const result = await apiGet(apiPath.reportOvertime, payload)
      
      if (result?.data?.success) {
        let finalData = []
        const rawResults = result.data.results
        
        if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
          finalData = rawResults.results
          setTotalRecords(rawResults.total || 0)
        } else if (Array.isArray(rawResults)) {
          finalData = rawResults
          setTotalRecords(rawResults.length)
        }
        setData(finalData || [])
      } else {
        setData([])
        setTotalRecords(0)
      }
    } catch (error) {
      console.error('Error fetching overtime report:', error)
      notification.error('Error fetching report data')
      setData([])
      setTotalRecords(0)
    } finally {
      setIsLoading(false)
    }
  }

  // ═══ HANDLE EXPORT ═══
  const handleExport = useCallback(async () => {
    if (isExportingRef.current) return;
    
    isExportingRef.current = true;
    setIsExporting(true);
    
    try {
      const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
      const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
      const employeeIDs = selectedEmployees.map(e => e.value)
      
      const payload = {
        startDate: start,
        endDate: end,
        export: true,
        ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
      }
      
      const result = await apiGet(apiPath.reportOvertime, payload)
      
      if (result?.data?.success) {
        let allData = []
        const rawResults = result.data.results
        if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
          allData = rawResults.results
        } else if (Array.isArray(rawResults)) {
          allData = rawResults
        }
        
        if (allData.length > 0) {
          exportReportToExcel(allData, 'Overtime Analysis', 'overtime_analysis')
        } else {
          notification.error('No data available to export')
        }
      } else {
        notification.error('Failed to fetch all data for export')
      }
    } catch (error) {
      console.error('Export failed:', error)
      notification.error('Export failed')
    } finally {
      setIsExporting(false)
      isExportingRef.current = false;
    }
  }, [dateRange, selectedEmployees, notification])

  // ═══ REGISTER EXPORT FUNCTION WITH PARENT ═══
  useEffect(() => {
    if (onExportReady && !isRegistered.current && data.length > 0) {
      onExportReady(handleExport);
      isRegistered.current = true;
    }
    
    return () => {
      if (onExportReady && isRegistered.current) {
        onExportReady(null);
        isRegistered.current = false;
      }
    }
  }, [handleExport, onExportReady, data]);

  // ═══ PAGINATION CALCULATIONS ═══
  const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE)

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  if (isLoading) return <LoadingState />;
  if (!data.length && !isLoading) return <EmptyState />;

  // Calculate Totals for Summary Cards
  const totalOTHours = data.reduce((sum, r) => sum + (r.totalOvertimeHours || 0), 0)
  const totalOTCost = data.reduce((sum, r) => sum + (r.overtimeCost || 0), 0)
  
  const highestOTEmployee = data.reduce((max, r) =>
    (r.totalOvertimeHours || 0) > (max.totalOvertimeHours || 0) ? r : max, data[0])

  // ═══ CHART 1: DUAL AXIS BAR CHART (INCLUDES ALL EMPLOYEES) ═══
  // We map ALL employees so empty bars show up for 0 OT.
  const barChartOptions = {
    indexAxis: 'y', // Horizontal
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.dataset.label === 'OT Hours') {
              return `OT Hours: ${context.parsed.x.toFixed(1)}h`;
            }
            return `OT Cost: $${context.parsed.x.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      x: { 
        grid: { display: false }, 
        ticks: { font: { size: 10 } } 
      },
      y: { 
        grid: { display: false }, 
        ticks: { font: { size: 10 } } 
      },
      // DUAL AXIS CONFIGURATION:
      x1: {
        position: 'top',
        grid: { display: false },
        ticks: { 
          callback: (val) => `$${val}`,
          font: { size: 10 } 
        }
      }
    }
  };

  const barChartData = {
    labels: data.map(item => item.employeeName || 'Unknown'),
    datasets: [
      {
        label: 'OT Hours',
        data: data.map(item => item.totalOvertimeHours || 0),
        backgroundColor: '#FCA5A5', // Light Coral (Pink)
        borderColor: '#F87171',
        borderWidth: 1,
        borderRadius: 3,
        // Use the LEFT x-axis (index 0)
        xAxisID: 'x',
      },
      {
        label: 'OT Cost ($)',
        data: data.map(item => item.overtimeCost || 0),
        backgroundColor: '#FCD34D', // Soft Yellow/Gold
        borderColor: '#FBBF24',
        borderWidth: 1,
        borderRadius: 3,
        // Use the RIGHT x-axis (index 1)
        xAxisID: 'x1',
      },
    ],
  };

  // ═══ CHART 2: OT COST DISTRIBUTION (DOUGHNUT) ═══
  // Only filter for the Doughnut chart, so it stays clean
  const doughnutData = data.filter(item => item.overtimeCost > 0);

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: $${context.parsed.toFixed(2)} (${percentage}%)`;
          }
        }
      }
    }
  };

  const doughnutChartData = {
    labels: doughnutData.map(item => item.employeeName),
    datasets: [
      {
        label: 'OT Cost',
        data: doughnutData.map(item => item.overtimeCost),
        backgroundColor: [
          '#F87171', '#FB923C', '#FBBF24', '#A78BFA', '#60A5FA', '#34D399'
        ], // Softer, modern colors (Coral, Orange, Yellow, Purple, Blue, Green)
        borderWidth: 3,
        borderColor: '#ffffff'
      },
    ],
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className='grid grid-cols-3 gap-4 mb-6'>
        <SummaryCard label='Total OT Hours' value={`${totalOTHours.toFixed(1)}h`} color='orange' />
        <SummaryCard label='Total OT Cost' value={`$${totalOTCost.toFixed(2)}`} color='red' />
        <SummaryCard label='Highest OT' value={highestOTEmployee?.employeeName || 'N/A'} color='purple' />
      </div>

      {/* ═══ CHARTS SECTION ═══ */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
        
        {/* Chart 1: Dual Axis Bar Chart (Includes 0 values visually) */}
        <div className='bg-white border border-[#E1E6EE] rounded-lg p-4 shadow-sm'>
          <h3 className='text-sm font-semibold text-gray-700 mb-3'>OT Hours & Cost per Employee</h3>
          <div className='h-64 w-full'>
            <Bar options={barChartOptions} data={barChartData} />
          </div>
        </div>

        {/* Chart 2: Cost Distribution (Only shows positive values) */}
        <div className='bg-white border border-[#E1E6EE] rounded-lg p-4 shadow-sm'>
          <h3 className='text-sm font-semibold text-gray-700 mb-3'>OT Cost Distribution</h3>
          <div className='h-64 w-full'>
            {doughnutData.length > 0 ? (
              <Doughnut options={doughnutOptions} data={doughnutChartData} />
            ) : (
              <div className='flex items-center justify-center h-full text-gray-400 text-sm'>
                No OT Cost data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══ TABLE ═══ */}
      <div className='overflow-x-auto rounded-lg border border-[#E1E6EE]'>
        <table className='w-full text-xs text-left text-[#6c6c6c]'>
          <thead className='text-xs text-gray-900 bg-[#EBEFF8]'>
            <tr>
              <th className='py-3 px-4 font-medium'>Sr no.</th>
              <th className='py-3 px-4 font-medium'>Employee</th>
              <th className='py-3 px-4 font-medium'>Regular Hrs</th>
              <th className='py-3 px-4 font-medium'>OT Hrs</th>
              <th className='py-3 px-4 font-medium'>OT Rate</th>
              <th className='py-3 px-4 font-medium'>OT Cost</th>
              <th className='py-3 px-4 font-medium'>OT Days</th>
              <th className='py-3 px-4 font-medium'>Total Days</th>
              <th className='py-3 px-4 font-medium'>OT % of Total Days</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              const otPercent = row.totalWorkedDays > 0 
                ? ((row.overtimeWorkedDays / row.totalWorkedDays) * 100).toFixed(1) 
                : 0;

              return (
                <tr key={i} className='bg-white border-b border-gray-100 hover:bg-gray-50 transition-colors'>
                  <td className='py-3 px-4 font-medium text-gray-800'>{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</td>
                  <td className='py-3 px-4 font-medium text-gray-800'>
                    <div>{row.employeeName}</div>
                    <div className='text-gray-400 text-xs'>{row.employeeId}</div>
                  </td>
                  <td className='py-3 px-4 text-gray-600'>{(row.regularHours || 0).toFixed(1)}h</td>
                  <td className='py-3 px-4'>
                    <span className={`font-semibold ${(row.totalOvertimeHours || 0) > 0 ? 'text-orange-500' : 'text-gray-400'}`}>
                      {(row.totalOvertimeHours || 0).toFixed(1)}h
                    </span>
                  </td>
                  <td className='py-3 px-4 text-gray-600'>${(row.overtimeRate || 0).toFixed(2)}/hr</td>
                  <td className='py-3 px-4 font-semibold text-orange-500'>
                    ${(row.overtimeCost || 0).toFixed(2)}
                  </td>
                  <td className='py-3 px-4 text-gray-600'>{row.overtimeWorkedDays || 0}</td>
                  <td className='py-3 px-4 text-gray-600'>{row.totalWorkedDays || 0}</td>
                  <td className='py-3 px-4'>
                    <div className='flex items-center gap-2'>
                      <div className='w-16 bg-gray-200 rounded-full h-1.5'>
                        <div
                          className='bg-orange-400 h-1.5 rounded-full'
                          style={{ width: `${Math.min(otPercent, 100)}%` }}
                        />
                      </div>
                      <span className='text-xs font-medium'>{otPercent}%</span>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ═══ PAGINATION FOOTER ═══ */}
      {totalPages > 1 && (
        <div className='flex items-center justify-between mt-4 pt-2 me-8 border-t border-gray-100'>
          <div className='text-sm text-gray-500'>
            Total <span className='font-medium text-gray-700'>{totalRecords}</span> Records
          </div>
          <div className='flex items-center gap-1'>
            <button
              onClick={() => currentPage === 1 ? null : handlePageChange(currentPage - 1)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors border ${
                currentPage === 1 
                  ? 'text-gray-300 cursor-not-allowed bg-transparent' 
                  : 'text-gray-600 bg-transparent hover:bg-gray-100'
              }`}
            >
              Prev
            </button>
            
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`w-8 h-8 rounded text-sm font-medium flex items-center border justify-center transition-colors ${
                    currentPage === pageNum
                      ? 'bg-[#1E40AF] text-white'
                      : 'text-gray-600 bg-transparent hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
            
            <button
              onClick={() => currentPage === totalPages ? null : handlePageChange(currentPage + 1)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                currentPage === totalPages 
                  ? 'text-gray-300 cursor-not-allowed bg-transparent' 
                  : 'text-gray-600 bg-transparent hover:bg-gray-100'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default OvertimeReport





// import React, { useEffect, useState, useCallback, useRef } from 'react'
// import { apiGet } from 'utils/apiFetch'
// import apiPath from 'utils/apiPath'
// import dayjs from 'dayjs'
// import useToastContext from 'hooks/useToastContext'
// import { exportReportToExcel } from 'utils/reportExport'
// import { SummaryCard, LoadingState, EmptyState } from './ScheduledVsActualReport'

// // ═══ PAGINATION CONSTANTS ═══
// const ITEMS_PER_PAGE = 10;

// const OvertimeReport = ({ selectedEmployees, dateRange, onExportReady }) => {
//   const [data, setData] = useState([])
//   const [totalRecords, setTotalRecords] = useState(0)
//   const [isLoading, setIsLoading] = useState(false)
//   const [isExporting, setIsExporting] = useState(false)
//   const notification = useToastContext()

//   // ═══ PAGINATION STATE ═══
//   const [currentPage, setCurrentPage] = useState(1)
  
//   // ═══ REFS ═══
//   const isRegistered = useRef(false);
//   const isExportingRef = useRef(false);

//   useEffect(() => {
//     const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
//     const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
//     fetchData(start, end, currentPage)
//   }, [selectedEmployees, dateRange, currentPage])

//   // ═══ RESET PAGE TO 1 WHENEVER FILTER CHANGES ═══
//   useEffect(() => {
//     setCurrentPage(1)
//   }, [selectedEmployees, dateRange])

//   const fetchData = async (start, end, page) => {
//     setIsLoading(true)
//     try {
//       const employeeIDs = selectedEmployees.map(e => e.value)
//       const payload = {
//         startDate: start,
//         endDate: end,
//         page: page,
//         limit: ITEMS_PER_PAGE,
//         ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
//       }
//       const result = await apiGet(apiPath.reportOvertime, payload)
      
//       if (result?.data?.success) {
//         let finalData = []
//         const rawResults = result.data.results
        
//         if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
//           finalData = rawResults.results
//           setTotalRecords(rawResults.total || 0)
//         } else if (Array.isArray(rawResults)) {
//           finalData = rawResults
//           setTotalRecords(rawResults.length)
//         }
//         setData(finalData || [])
//       } else {
//         setData([])
//         setTotalRecords(0)
//       }
//     } catch (error) {
//       console.error('Error fetching overtime report:', error)
//       notification.error('Error fetching report data')
//       setData([])
//       setTotalRecords(0)
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   // ═══ HANDLE EXPORT ═══
//   const handleExport = useCallback(async () => {
//     if (isExportingRef.current) return;
    
//     isExportingRef.current = true;
//     setIsExporting(true);
    
//     try {
//       const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
//       const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
//       const employeeIDs = selectedEmployees.map(e => e.value)
      
//       const payload = {
//         startDate: start,
//         endDate: end,
//         export: true,
//         ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
//       }
      
//       const result = await apiGet(apiPath.reportOvertime, payload)
      
//       if (result?.data?.success) {
//         let allData = []
//         const rawResults = result.data.results
//         if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
//           allData = rawResults.results
//         } else if (Array.isArray(rawResults)) {
//           allData = rawResults
//         }
        
//         if (allData.length > 0) {
//           exportReportToExcel(allData, 'Overtime Analysis', 'overtime_analysis')
//         } else {
//           notification.error('No data available to export')
//         }
//       } else {
//         notification.error('Failed to fetch all data for export')
//       }
//     } catch (error) {
//       console.error('Export failed:', error)
//       notification.error('Export failed')
//     } finally {
//       setIsExporting(false)
//       isExportingRef.current = false;
//     }
//   }, [dateRange, selectedEmployees, notification])

//   // ═══ REGISTER EXPORT FUNCTION WITH PARENT ═══
//   useEffect(() => {
//     if (onExportReady && !isRegistered.current && data.length > 0) {
//       onExportReady(handleExport);
//       isRegistered.current = true;
//     }
    
//     return () => {
//       if (onExportReady && isRegistered.current) {
//         onExportReady(null);
//         isRegistered.current = false;
//       }
//     }
//   }, [handleExport, onExportReady, data]);

//   // ═══ PAGINATION CALCULATIONS ═══
//   const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE)

//   const handlePageChange = (newPage) => {
//     if (newPage >= 1 && newPage <= totalPages) {
//       setCurrentPage(newPage)
//     }
//   }

//   if (isLoading) return <LoadingState />;
//   if (!data.length && !isLoading) return <EmptyState />;

//   // Calculate Totals for Summary Cards
//   const totalOTHours = data.reduce((sum, r) => sum + (r.totalOvertimeHours || 0), 0)
//   const totalOTCost = data.reduce((sum, r) => sum + (r.overtimeCost || 0), 0)
  
//   const highestOTEmployee = data.reduce((max, r) =>
//     (r.totalOvertimeHours || 0) > (max.totalOvertimeHours || 0) ? r : max, data[0])

//   return (
//     <div>
//       {/* Summary Cards */}
//       <div className='grid grid-cols-3 gap-4 mb-4'>
//         <SummaryCard label='Total OT Hours' value={`${totalOTHours.toFixed(1)}h`} color='orange' />
//         <SummaryCard label='Total OT Cost' value={`$${totalOTCost.toFixed(2)}`} color='red' />
//         <SummaryCard label='Highest OT' value={highestOTEmployee?.employeeName || 'N/A'} color='purple' />
//       </div>

//       {/* ═══ TABLE ═══ */}
//       <div className='overflow-x-auto rounded-lg border border-[#E1E6EE]'>
//         <table className='w-full text-xs text-left text-[#6c6c6c]'>
//           <thead className='text-xs text-gray-900 bg-[#EBEFF8]'>
//             <tr>
//               <th className='py-3 px-4 font-medium'>Sr no.</th>
//               <th className='py-3 px-4 font-medium'>Employee</th>
//               <th className='py-3 px-4 font-medium'>Regular Hrs</th>
//               <th className='py-3 px-4 font-medium'>OT Hrs</th>
//               <th className='py-3 px-4 font-medium'>OT Rate</th>
//               <th className='py-3 px-4 font-medium'>OT Cost</th>
//               <th className='py-3 px-4 font-medium'>OT Days</th>
//               <th className='py-3 px-4 font-medium'>Total Days</th>
//               <th className='py-3 px-4 font-medium'>OT % of Total Days</th>
//             </tr>
//           </thead>
//           <tbody>
//             {data.map((row, i) => {
//               // CALCULATION FIX: % of Total Days Worked (Homebase style)
//               const otPercent = row.totalWorkedDays > 0 
//                 ? ((row.overtimeWorkedDays / row.totalWorkedDays) * 100).toFixed(1) 
//                 : 0;

//               return (
//                 <tr key={i} className='bg-white border-b border-gray-100 hover:bg-gray-50 transition-colors'>
//                   <td className='py-3 px-4 font-medium text-gray-800'>{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</td>
//                   <td className='py-3 px-4 font-medium text-gray-800'>
//                     <div>{row.employeeName}</div>
//                     <div className='text-gray-400 text-xs'>{row.employeeId}</div>
//                   </td>
//                   <td className='py-3 px-4 text-gray-600'>{(row.regularHours || 0).toFixed(1)}h</td>
//                   <td className='py-3 px-4'>
//                     <span className={`font-semibold ${(row.totalOvertimeHours || 0) > 0 ? 'text-orange-500' : 'text-gray-400'}`}>
//                       {(row.totalOvertimeHours || 0).toFixed(1)}h
//                     </span>
//                   </td>
//                   <td className='py-3 px-4 text-gray-600'>${(row.overtimeRate || 0).toFixed(2)}/hr</td>
//                   <td className='py-3 px-4 font-semibold text-orange-500'>
//                     ${(row.overtimeCost || 0).toFixed(2)}
//                   </td>
//                   <td className='py-3 px-4 text-gray-600'>{row.overtimeWorkedDays || 0}</td>
//                   <td className='py-3 px-4 text-gray-600'>{row.totalWorkedDays || 0}</td>
//                   <td className='py-3 px-4'>
//                     <div className='flex items-center gap-2'>
//                       <div className='w-16 bg-gray-200 rounded-full h-1.5'>
//                         <div
//                           className='bg-orange-400 h-1.5 rounded-full'
//                           style={{ width: `${Math.min(otPercent, 100)}%` }}
//                         />
//                       </div>
//                       <span className='text-xs font-medium'>{otPercent}%</span>
//                     </div>
//                   </td>
//                 </tr>
//               )
//             })}
//           </tbody>
//         </table>
//       </div>

//       {/* ═══ PAGINATION FOOTER ═══ */}
//       {totalPages > 1 && (
//         <div className='flex items-center justify-between mt-4 pt-2 me-8 border-t border-gray-100'>
//           <div className='text-sm text-gray-500'>
//             Total <span className='font-medium text-gray-700'>{totalRecords}</span> Records
//           </div>
//           <div className='flex items-center gap-1'>
//             <button
//               onClick={() => currentPage === 1 ? null : handlePageChange(currentPage - 1)}
//               className={`px-3 py-1 rounded text-sm font-medium transition-colors border ${
//                 currentPage === 1 
//                   ? 'text-gray-300 cursor-not-allowed bg-transparent' 
//                   : 'text-gray-600 bg-transparent hover:bg-gray-100'
//               }`}
//             >
//               Prev
//             </button>
            
//             {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
//               let pageNum;
//               if (totalPages <= 5) {
//                 pageNum = i + 1;
//               } else if (currentPage <= 3) {
//                 pageNum = i + 1;
//               } else if (currentPage >= totalPages - 2) {
//                 pageNum = totalPages - 4 + i;
//               } else {
//                 pageNum = currentPage - 2 + i;
//               }
              
//               return (
//                 <button
//                   key={pageNum}
//                   onClick={() => handlePageChange(pageNum)}
//                   className={`w-8 h-8 rounded text-sm font-medium flex items-center border justify-center transition-colors ${
//                     currentPage === pageNum
//                       ? 'bg-[#1E40AF] text-white'
//                       : 'text-gray-600 bg-transparent hover:bg-gray-100'
//                   }`}
//                 >
//                   {pageNum}
//                 </button>
//               )
//             })}
            
//             <button
//               onClick={() => currentPage === totalPages ? null : handlePageChange(currentPage + 1)}
//               className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
//                 currentPage === totalPages 
//                   ? 'text-gray-300 cursor-not-allowed bg-transparent' 
//                   : 'text-gray-600 bg-transparent hover:bg-gray-100'
//               }`}
//             >
//               Next
//             </button>
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }

// export default OvertimeReport